import styled from 'styled-components'
import { logOverlayRendered, logUnrecognisedOverlay } from '../../../common/utils'
import { OpenBankingOverlay } from './components/open-banking-overlay'
import { ActivityCallback, EventPayload, MessagePayload } from '../../types/common'
import { useEffect, useState } from 'react'
import { ModelType } from '../../types/model'
import { ActivitySubscription, OverlayComponentProps } from './types'
import { DefaultHeader } from './components/default-header'
import { MultiFileUploadOverlay } from './components/multi-file-upload-overlay'
import { AddressCaptureOverlay } from './components/address-capture-overlay'
import { IdentityUploadOverlay } from './components/identity-document-overlay'
import { RightToRentOverlay } from './components/right-to-rent-overlay'
import { CurrencyDetailsOverlay } from './components/currency-details-overlay'
import { AdditionalDetailsOverlay } from './components/additional-details-overlay'
import { SubjectDetailsOverlay } from './components/subject-details-overlay'
import { TermsAndConditionsOverlay } from './components/terms-and-conditions-overlay'

const StyledOverlayContainer = styled.div<{ $containScroll?: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1001;
  overflow-y: ${({ $containScroll }) => ($containScroll ? 'hidden' : 'scroll')};
  animation: slide-up 1s cubic-bezier(0, 1, 0.5, 1) 500ms backwards;
  border-radius: 10px;
  background-color: #ffffff;
  @keyframes slide-up {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }
`

const StyledOverlay = styled.div`
  position: relative;
  height: 100%;
`

export interface Props {
  model: ModelType
  onClose: () => void
  onOverlaySubmit: () => void
  onSendEvent: (payload: EventPayload) => void
  onSendMessage: (payload: MessagePayload) => void
  onRegisterActivityCallback: (callback: ActivityCallback) => ActivitySubscription
  hasUploadedFiles?: boolean
  container: string
  sas: string
  uri: string
}

const renderOverlayComponent = ({
  model,
  ...props
}: OverlayComponentProps<ModelType>) => {
  switch (model.modal) {
    case 'open-banking-o-auth':
      return (
        <OpenBankingOverlay
          {...props}
          model={model}
          renderOverlayHeader={() => (
            <DefaultHeader
              modal={model.modal}
              isMobile={props.isMobile}
              title="Verify your income by connecting a bank account"
              onClose={() => {
                props.onSendEvent({ name: 'close' })
                props.onClose()
              }}
            />
          )}
        />
      )
    case 'multi-file-upload':
      return (
        <MultiFileUploadOverlay
          {...props}
          model={model}
          onClose={props.onClose}
          renderOverlayHeader={() => (
            <DefaultHeader
              modal={model.modal}
              isMobile={props.isMobile}
              title={`${
                model.overlayTitle !== undefined
                  ? model.overlayTitle
                  : 'Upload your documents'
              }`}
              onClose={props.onClose}
            />
          )}
        />
      )
    case 'address-capture':
      return (
        <AddressCaptureOverlay
          {...props}
          model={model}
          renderOverlayHeader={() => (
            <DefaultHeader
              modal={model.modal}
              isMobile={props.isMobile}
              title={model.overlayTitle}
              onClose={props.onClose}
            />
          )}
        />
      )
    case 'proof-of-identity-overlay':
      return (
        <IdentityUploadOverlay
          {...props}
          model={model}
          renderOverlayHeader={() => (
            <DefaultHeader
              modal={model.modal}
              isMobile={props.isMobile}
              title={model.overlayTitle}
              onClose={props.onClose}
            />
          )}
        />
      )
    case 'right-to-rent':
      return (
        <RightToRentOverlay
          {...props}
          model={model}
          renderOverlayHeader={() => (
            <DefaultHeader
              modal={model.modal}
              isMobile={props.isMobile}
              title={model.overlayTitle}
              onClose={props.onClose}
            />
          )}
        />
      )
    case 'additional-details':
      return (
        <AdditionalDetailsOverlay
          {...props}
          model={model}
          renderOverlayHeader={() => (
            <DefaultHeader
              modal={model.modal}
              isMobile={props.isMobile}
              title={model.overlayTitle}
              onClose={props.onClose}
            />
          )}
        />
      )
    case 'currency-details':
      return (
        <CurrencyDetailsOverlay
          {...props}
          model={model}
          renderOverlayHeader={() => (
            <DefaultHeader
              modal={model.modal}
              isMobile={props.isMobile}
              title={model.overlayTitle}
              onClose={props.onClose}
            />
          )}
        />
      )
    case 'subject-details':
      return (
        <SubjectDetailsOverlay
          {...props}
          model={model}
          renderOverlayHeader={() => (
            <DefaultHeader
              modal={model.modal}
              isMobile={props.isMobile}
              title={model.overlayTitle}
              onClose={props.onClose}
            />
          )}
        />
      )
    case 'terms-and-conditions':
      return <TermsAndConditionsOverlay {...props} model={model} />
    default:
      return null
  }
}

export const Overlay = ({
  model,
  onClose,
  onSendEvent,
  onSendMessage,
  onRegisterActivityCallback,
  onOverlaySubmit,
  container,
  sas,
  uri,
}: Props) => {
  const [isMobile, setIsMobile] = useState(false)

  const handleCloseOverlay = () => {
    onClose()
  }

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 800)
    }

    handleResize()

    addEventListener('resize', handleResize)

    return () => {
      removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    const modalName: string = model.modal

    switch (model.modal) {
      case 'open-banking-o-auth':
      case 'multi-file-upload':
      case 'address-capture':
      case 'proof-of-identity-overlay':
      case 'right-to-rent':
      case 'currency-details':
      case 'additional-details':
      case 'subject-details':
      case 'terms-and-conditions':
        logOverlayRendered(modalName)
        break
      default:
        logUnrecognisedOverlay(modalName)
    }
  }, [model.modal])

  return (
    <StyledOverlayContainer
      data-testid="overlay"
      $containScroll={model.modal === 'terms-and-conditions'}
    >
      <StyledOverlay>
        {renderOverlayComponent({
          model,
          onClose: handleCloseOverlay,
          onOverlaySubmit,
          onSendEvent,
          onSendMessage,
          onRegisterActivityCallback,
          isMobile,
          container,
          sas,
          uri,
        })}
      </StyledOverlay>
    </StyledOverlayContainer>
  )
}
